import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { map, Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { GmMatColumnDef } from "../components/editable-table/GmMatColumnDef";
import { AbstractControl, ValidatorFn } from "@angular/forms";
import { Annnouncement } from "../components/utility/utility-classes";

@Injectable({
  providedIn: "root",
})
export class ReaderService {
  constructor(private readonly http: HttpClient) {}

  getAllEnabledFromTable(table: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${environment.serviceURL}/crud/enabled?table=${table}`
    );
  }

  getAllFromTable(table: string): Observable<any[]> {
    return this.http.get<any[]>(`${environment.serviceURL}/crud/rest/${table}`);
  }

  getRegions(view: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${environment.serviceURL}/regions?view=${view}`
    );
  }

  getDistinctFromTable(table: string, column: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${environment.serviceURL}/crud/distinct?table=${table}&column=${column}`
    );
  }

  getAllRecords(view: string): Observable<any[]> {
    return this.http.get<any[]>(`${environment.serviceURL}/${view}`);
  }

  getAllFields(view: string): Observable<GmMatColumnDef[]> {
    const url = `${environment.serviceURL}/headers?view=${view}`;
    return this.http
      .get<
        {
          header: string;
          field: string;
          display: boolean;
          lock: boolean;
          index: number;
          tooltip: string;
          type: string;
        }[]
      >(url)
      .pipe(
        map((hHeaders) =>
          hHeaders.map(
            (header) =>
              new GmMatColumnDef(
                header.field,
                header.header,
                header.display,
                header.lock,
                header.index,
                header.tooltip,
                header.type
              )
          )
        )
      );
  }

  getVehicleData(id: string): Observable<any> {
    return this.http.get<any>(
      `${environment.serviceURL}/vehicle?vehicleId=${id}`
    );
  }

  getVehicleBodystyle(id: string): Observable<any> {
    return this.http.get<any>(
      `${environment.serviceURL}/bodystyle?vehicleId=${id}`
    );
  }

  getVehiclePropulsions(id: string): Observable<any> {
    return this.http.get<any>(
      `${environment.serviceURL}/propulsion?vehicleId=${id}`
    );
  }

  getVehicleAttachments(id: string): Observable<any> {
    return this.http.get<any>(
      `${environment.serviceURL}/attachments?vehicleId=${id}`
    );
  }

  getVehicleOems(): Observable<any> {
    return this.http.get<any>(
      `${environment.serviceURL}/competitiveLandscapeAvailableOems`
    );
  }

  getVehicleBrands(): Observable<any> {
    return this.http.get<any>(
      `${environment.serviceURL}/competitiveLandscapeAvailableBrands`
    );
  }

  getAvailableSegments(): Observable<any> {
    return this.http.get<any>(
      `${environment.serviceURL}/competitiveLandscapeAvailableSegments`
    );
  }

  getBrandForOem(oem: string): Observable<any> {
    return this.http.get<any>(
      `${environment.serviceURL}/brandForOem?oem=${encodeURIComponent(oem)}`
    );
  }

  getActiveBrandForOem(oem: any): Observable<any> {
    const url = `${environment.serviceURL}/activeBrandForOem`;
    return this.http.post<any>(url, oem);
  }

  getActiveModel(): Observable<any> {
    return this.http.get<any>(
      `${environment.serviceURL}/activeGlobalNameplate`
    );
  }

  getModelsForBrands(oem: string[], brand: any[]): Observable<any> {
    const url = `${environment.serviceURL}/modelsForBrands`;
    return this.http.post<any>(url, {
      fromDate: null,
      toDate: null,
      displayYears: null,
      oem,
      brand,
      model: null,
      segment: null,
      propulsion: null,
      market: null,
    });
  }

  getSegmentsForModels(
    oem: string[],
    brand: string[],
    model: string[]
  ): Observable<any> {
    const url = `${environment.serviceURL}/segmentsForModel`;
    return this.http.post<any>(url, {
      fromDate: null,
      toDate: null,
      displayYears: null,
      oem,
      brand,
      model,
      segment: null,
      propulsion: null,
      market: null,
    });
  }

  getRegionForSegments(segment: string): Observable<any> {
    return this.http.get<any>(
      `${environment.serviceURL}/regionForSegment?segment=${encodeURIComponent(
        segment
      )}`
    );
  }

  getHomeImages(): Observable<any> {
    return this.http.get<any>(`${environment.serviceURL}/homeImages`);
  }

  /** Fetches an image via authenticated HttpClient and returns a blob object URL. */
  getAuthenticatedImageUrl(image: string): Observable<string> {
    return this.http
      .get(`${environment.serviceURL}/images?image=${encodeURIComponent(image)}`, {
        responseType: "blob",
      })
      .pipe(map((blob) => URL.createObjectURL(blob)));
  }

  getAttachment(filename: string): Observable<Blob> {
    return this.http.get(
      `${environment.serviceURL}/attachment?filename=${encodeURIComponent(
        filename
      )}`,
      { responseType: "blob" }
    );
  }

  getPhoto(filename: string): Observable<Blob> {
    return this.http.get(
      `${environment.serviceURL}/photo?filename=${encodeURIComponent(filename)}`,
      { responseType: "blob" }
    );
  }

  getHelpDocument(doc: string): Observable<Blob> {
    return this.http.get(
      `${environment.serviceURL}/help-doc?doc=${encodeURIComponent(doc)}`,
      { responseType: "blob" }
    );
  }

  getDistinctVehicles(): Observable<any> {
    return this.http.get<any>(`${environment.serviceURL}/distinctVehicles`);
  }

  getUserColumnForViews(gminId: string, viewName: string): Observable<any> {
    return this.http.get<any>(
      `${environment.serviceURL}/userViewColumn?gminId=${gminId}&viewName=${viewName}`
    );
  }

  _filterAutoComplete(value: any, options: any[]): any[] {
    return options.filter(
      (option) => option.toLowerCase().indexOf(value.toLowerCase()) === 0
    );
  }

  getAnnouncement(): Observable<Annnouncement> {
    return this.http.get<Annnouncement>(
      `${environment.serviceURL}/announcement`
    );
  }
}

/** Selected value HAS to exists */
export function optionValidator(options: string[]): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    return !options.includes(control.value)
      ? { incorrectOption: { value: control.value } }
      : null;
  };
}
